import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { submissionsApi, tournamentsApi } from '../services/api';
import { DeckSubmission } from '../types';
import RejectionModal from '../components/RejectionModal';
import { useAuth } from '../context/AuthContext';

interface DeckCard {
  card_name: string;
  set_code: string | null;
  quantity: number;
  price: number | null;
  total_price: number | null;
}

interface DeckBreakdown {
  submission_id: string;
  deck_name: string;
  total_cost: number;
  cards: DeckCard[];
}

const DeckDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState<DeckSubmission | null>(null);
  const [breakdown, setBreakdown] = useState<DeckBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [viewAs, setViewAs] = useState<'player' | 'to'>('player');
  const [isOwner, setIsOwner] = useState(false);
  const [isTo, setIsTo] = useState(false);

  const loadDeckDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both submission details and breakdown
      const [submissionResponse, breakdownResponse] = await Promise.all([
        submissionsApi.getById(id!),
        submissionsApi.getBreakdown(id!)
      ]);

      setSubmission(submissionResponse.data);
      setBreakdown(breakdownResponse.data);

      // Determine user permissions
      const currentUserId = user?.id || 0;
      const isOwnSubmission = submissionResponse.data.user_id === currentUserId;
      setIsOwner(isOwnSubmission);

      // Check if user is TO for this tournament
      let isTournamentOrganizer = false;
      if (submissionResponse.data.tournament_id) {
        try {
          console.log('Checking TO status for tournament:', submissionResponse.data.tournament_id);
          console.log('Current user ID:', currentUserId);
          const organizersResponse = await tournamentsApi.getOrganizers(submissionResponse.data.tournament_id);
          console.log('Tournament organizers:', organizersResponse.data);
          isTournamentOrganizer = organizersResponse.data.some(org => org.user_id === currentUserId);
          console.log('Is tournament organizer:', isTournamentOrganizer);
          setIsTo(isTournamentOrganizer);
        } catch (error) {
          console.error('Could not check TO status:', error);
          setIsTo(false);
        }
      }
      
      console.log('isOwnSubmission:', isOwnSubmission);
      console.log('isTournamentOrganizer:', isTournamentOrganizer);
      console.log('Submission status:', submissionResponse.data.status);

      // Set default view
      if (isOwnSubmission && isTournamentOrganizer) {
        // If user is both owner and TO, choose view based on deck status
        if (submissionResponse.data.status === 'DRAFT') {
          setViewAs('player'); // Default to player view for drafts (can edit/submit)
        } else {
          setViewAs('to'); // Default to TO view for submitted/approved/rejected (can review)
        }
      } else if (isTournamentOrganizer) {
        setViewAs('to');
      } else {
        setViewAs('player');
      }

    } catch (error: any) {
      console.error('Failed to load deck details:', error);
      setError('Failed to load deck details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadDeckDetails();
    }
  }, [id, loadDeckDetails]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return 'N/A';
    return `€${price.toFixed(2)}`;
  };

  const handleCopyDecklist = async () => {
    try {
      await navigator.clipboard.writeText(submission!.decklist_text);
      alert('Decklist copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy decklist:', err);
      alert('Failed to copy decklist to clipboard');
    }
  };

  const handleEditName = () => {
    if (submission) {
      setEditingName(submission.deck_name);
      setIsEditingName(true);
    }
  };

  const handleSaveName = async () => {
    if (!submission || !editingName.trim()) return;
    
    try {
      const response = await submissionsApi.update(submission.id, {
        deck_name: editingName.trim()
      });
      setSubmission(response.data);
      setIsEditingName(false);
    } catch (error: any) {
      console.error('Failed to update deck name:', error);
      alert('Failed to update deck name. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleDeleteDeck = async () => {
    if (!submission) return;
    
    if (!window.confirm('Are you sure you want to delete this deck? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await submissionsApi.delete(submission.id);
      navigate('/my-tournaments');
    } catch (error: any) {
      console.error('Failed to delete deck:', error);
      alert('Failed to delete deck. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmitDeck = async () => {
    if (!submission) return;

    try {
      setIsSubmitting(true);
      const response = await submissionsApi.submit(submission.id);
      setSubmission(response.data);
      alert('Deck submitted for review!');
    } catch (error: any) {
      console.error('Failed to submit deck:', error);
      alert('Failed to submit deck. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveDeck = async () => {
    if (!submission) return;

    try {
      setIsApproving(true);
      const response = await tournamentsApi.reviewSubmission(
        submission.tournament_id,
        submission.id,
        { status: 'APPROVED' }
      );
      setSubmission(response.data);
      alert('Deck approved!');
    } catch (error: any) {
      console.error('Failed to approve deck:', error);
      alert('Failed to approve deck. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectDeck = (rejectionReason: string) => {
    if (!submission) return;

    const reviewData = {
      status: 'REJECTED' as const,
      rejection_reason: rejectionReason || undefined
    };

    tournamentsApi.reviewSubmission(submission.tournament_id, submission.id, reviewData)
      .then(response => {
        setSubmission(response.data);
        setShowRejectionModal(false);
        alert('Deck rejected.');
      })
      .catch(error => {
        console.error('Failed to reject deck:', error);
        alert('Failed to reject deck. Please try again.');
      });
  };

  const toggleView = () => {
    setViewAs(viewAs === 'player' ? 'to' : 'player');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading deck details...</div>
        </div>
      </div>
    );
  }

  if (error || !submission || !breakdown) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">{error || 'Deck not found.'}</p>
          <button
            onClick={() => navigate('/my-tournaments')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to My Tournaments
          </button>
        </div>
      </div>
    );
  }

  const canEdit = isOwner && submission.status === 'DRAFT';
  const canSubmit = isOwner && submission.status === 'DRAFT';
  const canDelete = isOwner && submission.status === 'DRAFT';
  const canReview = isTo && submission.status === 'SUBMITTED';

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-start">
            <div>
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    className="text-3xl font-bold text-gray-900 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h1 className="text-3xl font-bold text-gray-900">{submission.deck_name}</h1>
                  {canEdit && (
                    <button
                      onClick={handleEditName}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                      title="Edit deck name"
                    >
                      ✏️
                    </button>
                  )}
                </div>
              )}
              <p className="text-gray-600 mt-1">
                {viewAs === 'to' ? (
                  <>Submitted by {submission.user?.username || `User ${submission.user_id}`} to {submission.tournament?.name || 'Tournament'}</>
                ) : (
                  <>Submitted to {submission.tournament?.name || 'Tournament'}</>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* View Toggle (only show if user is both owner and TO) */}
              {isOwner && isTo && (
                <button
                  onClick={toggleView}
                  className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm"
                >
                  {viewAs === 'player' ? 'Switch to TO View' : 'Switch to Player View'}
                </button>
              )}
              
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(submission.status)}`}>
                {submission.status}
              </span>
              
              {/* Player Actions */}
              {viewAs === 'player' && canDelete && (
                <button
                  onClick={handleDeleteDeck}
                  disabled={isDeleting}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Deck'}
                </button>
              )}
              
              {/* TO Actions */}
              {viewAs === 'to' && canReview && (
                <>
                  <button
                    onClick={handleApproveDeck}
                    disabled={isApproving}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => setShowRejectionModal(true)}
                    disabled={isRejecting}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Reject
                  </button>
                </>
              )}
              
              <button
                onClick={() => navigate(viewAs === 'to' ? '/tournament-management' : '/my-tournaments')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>

        {/* Submit Button (Player View Only) */}
        {viewAs === 'player' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Deck Submission</h3>
                <p className="text-gray-600">
                  {submission.status === 'DRAFT' 
                    ? 'Submit your deck for tournament review'
                    : `Status: ${submission.status}`
                  }
                </p>
                {submission.status === 'DRAFT' && submission.validation_errors && submission.validation_errors.length > 0 && (
                  <p className="text-red-600 text-sm mt-1">
                    Fix validation issues before submitting
                  </p>
                )}
              </div>
              <div className="flex space-x-3">
                {submission.status === 'DRAFT' ? (
                  <button
                    onClick={handleSubmitDeck}
                    disabled={isSubmitting || (submission.validation_errors && submission.validation_errors.length > 0)}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      (submission.validation_errors && submission.validation_errors.length > 0)
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                  </button>
                ) : (
                  <span className={`px-4 py-2 rounded-lg text-sm font-medium ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Deck Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Main Info */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Deck Overview</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Cost</label>
                  <p className="text-2xl font-bold text-green-600">
                    {formatPrice(submission.total_cost)}
                  </p>
                  {submission.tournament?.ruleset?.budget_limit && (
                    <p className="text-sm text-gray-600">
                      Budget: {formatPrice(submission.tournament.ruleset.budget_limit)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Card Count</label>
                  <p className="text-2xl font-bold text-blue-600">
                    {breakdown.cards.reduce((sum, card) => sum + card.quantity, 0)}
                  </p>
                </div>
              </div>

              {/* Budget Status Indicator */}
              {submission.tournament?.ruleset?.budget_limit && (
                <div className={`mb-4 p-3 rounded-lg ${
                  submission.total_cost <= submission.tournament.ruleset.budget_limit 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center">
                    <span className={`text-sm font-medium ${
                      submission.total_cost <= submission.tournament.ruleset.budget_limit 
                        ? 'text-green-800' 
                        : 'text-red-800'
                    }`}>
                      {submission.total_cost <= submission.tournament.ruleset.budget_limit 
                        ? '✅ Within Budget' 
                        : '❌ Over Budget'
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {submission.validation_errors && submission.validation_errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Validation Issues:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {submission.validation_errors.map((error, index) => {
                      const isFormatError = error.includes('not legal in');
                      const isBanlistError = error.includes('banned by this ruleset');
                      
                      return (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">
                            {isFormatError ? '⚠️' : isBanlistError ? '🚫' : '•'}
                          </span>
                          <span className={isFormatError ? 'font-medium' : isBanlistError ? 'font-medium' : ''}>
                            {error}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Rejection Reason (TO View) */}
              {viewAs === 'to' && submission.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Rejection Reason:</h4>
                  <p className="text-sm text-red-700">{submission.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* Tournament Info */}
            {submission.tournament && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tournament Info</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Tournament:</span>
                    <p className="text-sm text-gray-900">{submission.tournament.name}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Date:</span>
                    <p className="text-sm text-gray-900">
                      {new Date(submission.tournament.date).toLocaleDateString()}
                    </p>
                  </div>
                  {submission.tournament.ruleset && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Ruleset:</span>
                      <p className="text-sm text-gray-900">{submission.tournament.ruleset.name}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submission Info (TO View) */}
            {viewAs === 'to' && submission.user && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submitter Info</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Player:</span>
                    <p className="text-sm text-gray-900">{submission.user.username}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Email:</span>
                    <p className="text-sm text-gray-900">{submission.user.email}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Submitted:</span>
                    <p className="text-sm text-gray-900">
                      {new Date(submission.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Deck List</h2>
            <button
              onClick={handleCopyDecklist}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Copy Decklist
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Card Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Set
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {breakdown.cards.map((card, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {card.card_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.set_code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatPrice(card.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatPrice(card.total_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onReject={handleRejectDeck}
        deckName={submission.deck_name}
      />
    </div>
  );
};

export default DeckDetails;